import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, Download, RefreshCw, Users, Car, Building2, Tag, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { toLocalYmd } from '@/lib/dateRange';

// iter525 — Fines Report page.
// Shows a date-range aggregation of all fines, with breakdowns by
// employee, vehicle, branch, and fine_type. Each row drills down to
// the underlying fines. Consumed by the AI Branch-Loss analyzer for
// "fines by branch" drill-downs.
export default function FinesReportPage() {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(toLocalYmd(firstOfMonth));
  const [endDate, setEndDate] = useState(toLocalYmd(today));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const res = await api.get(`/fines/report?${params.toString()}`);
      setReport(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load fines report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const exportCSV = () => {
    if (!report?.rows?.length) { toast.info('No data to export'); return; }
    const headers = ['Date', 'Type', 'Department', 'Description', 'Amount', 'Paid', 'Outstanding', 'Status', 'Employee', 'Plate', 'Branch'];
    const lines = [headers.join(',')];
    for (const r of report.rows) {
      const line = [
        r.fine_date || '', r.fine_type || '', r.department || '',
        `"${(r.description || '').replace(/"/g, '""')}"`,
        r.amount, r.paid_amount, r.outstanding,
        r.payment_status || '', r.employee_name || '', r.plate_number || '', r.branch_name || '',
      ].join(',');
      lines.push(line);
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `fines_report_${startDate}_to_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const grand = report?.grand || { count: 0, total: 0, paid: 0, outstanding: 0 };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-600 to-pink-700 flex items-center justify-center shadow-lg shadow-rose-200/50">
              <AlertTriangle size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="fines-report-title">Fines Report</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Breakdown by employee, vehicle, branch, and type</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Link to="/fines" data-testid="back-to-fines-link">
              <Button variant="outline" size="sm" className="rounded-xl"><ArrowLeft size={14} className="mr-1" /> Back to Fines</Button>
            </Link>
            <Button variant="outline" size="sm" onClick={exportCSV} className="rounded-xl" data-testid="fines-report-export-csv">
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading} className="rounded-xl" data-testid="fines-report-refresh">
              <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Date range */}
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 w-44" data-testid="fines-report-start" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 w-44" data-testid="fines-report-end" />
              </div>
              <Button onClick={fetchReport} size="sm" className="rounded-xl" data-testid="fines-report-apply">Apply</Button>
            </div>
          </CardContent>
        </Card>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Total Fines</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold font-outfit text-rose-600">{grand.count}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Total Amount</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold font-outfit">{cur()} {grand.total.toFixed(2)}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Paid</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold font-outfit text-emerald-600">{cur()} {grand.paid.toFixed(2)}</div></CardContent></Card>
          <Card className="border-border"><CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Outstanding</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold font-outfit text-amber-600">{cur()} {grand.outstanding.toFixed(2)}</div></CardContent></Card>
        </div>

        {/* Breakdown tabs */}
        <Tabs defaultValue="by_employee">
          <TabsList>
            <TabsTrigger value="by_employee" data-testid="tab-by-employee"><Users size={14} className="mr-1" /> Employee</TabsTrigger>
            <TabsTrigger value="by_vehicle" data-testid="tab-by-vehicle"><Car size={14} className="mr-1" /> Vehicle</TabsTrigger>
            <TabsTrigger value="by_branch" data-testid="tab-by-branch"><Building2 size={14} className="mr-1" /> Branch</TabsTrigger>
            <TabsTrigger value="by_type" data-testid="tab-by-type"><Tag size={14} className="mr-1" /> Type</TabsTrigger>
            <TabsTrigger value="rows" data-testid="tab-all-rows">All Fines</TabsTrigger>
          </TabsList>

          {[
            { key: 'by_employee', label: 'Employee' },
            { key: 'by_vehicle', label: 'Vehicle / Plate' },
            { key: 'by_branch', label: 'Branch' },
            { key: 'by_type', label: 'Fine Type' },
          ].map(({ key, label }) => (
            <TabsContent key={key} value={key}>
              <Card className="border-border"><CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium">{label}</th>
                      <th className="text-right p-3 text-sm font-medium">Count</th>
                      <th className="text-right p-3 text-sm font-medium">Total</th>
                      <th className="text-right p-3 text-sm font-medium">Paid</th>
                      <th className="text-right p-3 text-sm font-medium">Outstanding</th>
                    </tr></thead>
                    <tbody>
                      {(report?.[key] || []).map((row) => (
                        <tr key={row.key} className="border-b hover:bg-muted/50" data-testid={`${key}-row`}>
                          <td className="p-3 text-sm font-medium">{row.label}</td>
                          <td className="p-3 text-sm text-right">{row.count}</td>
                          <td className="p-3 text-sm text-right font-mono">{cur()} {row.total.toFixed(2)}</td>
                          <td className="p-3 text-sm text-right text-emerald-600">{cur()} {row.paid.toFixed(2)}</td>
                          <td className="p-3 text-sm text-right text-amber-600">{cur()} {row.outstanding.toFixed(2)}</td>
                        </tr>
                      ))}
                      {(!report?.[key] || report[key].length === 0) && (
                        <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No data for this range</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent></Card>
            </TabsContent>
          ))}

          <TabsContent value="rows">
            <Card className="border-border"><CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b">
                    <th className="text-left p-3 text-sm font-medium">Date</th>
                    <th className="text-left p-3 text-sm font-medium">Type</th>
                    <th className="text-left p-3 text-sm font-medium">Description</th>
                    <th className="text-left p-3 text-sm font-medium">Employee</th>
                    <th className="text-left p-3 text-sm font-medium">Plate</th>
                    <th className="text-left p-3 text-sm font-medium">Branch</th>
                    <th className="text-right p-3 text-sm font-medium">Amount</th>
                    <th className="text-center p-3 text-sm font-medium">Status</th>
                  </tr></thead>
                  <tbody>
                    {(report?.rows || []).map((r) => (
                      <tr key={r.id} className="border-b hover:bg-muted/50" data-testid="fines-report-row">
                        <td className="p-3 text-sm">{r.fine_date ? r.fine_date.slice(0,10) : '—'}</td>
                        <td className="p-3"><Badge variant="secondary" className="capitalize text-xs">{r.fine_type}</Badge></td>
                        <td className="p-3 text-sm">{r.description}</td>
                        <td className="p-3 text-sm">{r.employee_name || <span className="text-muted-foreground">Company</span>}</td>
                        <td className="p-3 text-sm font-mono text-xs">{r.plate_number || '—'}</td>
                        <td className="p-3 text-sm">{r.branch_name || '—'}</td>
                        <td className="p-3 text-sm text-right font-bold">{cur()} {r.amount.toFixed(2)}</td>
                        <td className="p-3 text-center"><Badge className={r.payment_status === 'paid' ? 'bg-success/20 text-success' : r.payment_status === 'partial' ? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'}>{r.payment_status}</Badge></td>
                      </tr>
                    ))}
                    {(!report?.rows || report.rows.length === 0) && (
                      <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No fines in this date range</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
